import { Check } from 'lucide-react';
import { Button } from '../ui/Button.js';

export interface StepIndicatorStep {
  key: string;
  label: string;
}

export interface StepIndicatorProps {
  steps: ReadonlyArray<StepIndicatorStep>;
  currentStep: number;
  /** Zero-based index of the furthest step the user has reached. Steps beyond
   *  this index are locked (not clickable) until the user advances via the
   *  Next button + validation. */
  furthestReached: number;
  onJumpTo: (index: number) => void;
}

/**
 * Dot-trail step indicator for the entity wizard header. Current step is
 * highlighted; reached-but-not-current steps show a check and are clickable;
 * unreached steps are muted and locked.
 *
 * Per ENTITY_CREATION_WIZARD_SPEC.md §10.1.
 */
export function StepIndicator({ steps, currentStep, furthestReached, onJumpTo }: StepIndicatorProps) {
  return (
    <nav aria-label="Wizard steps" className="flex items-center gap-1 overflow-x-auto px-4 py-2 border-b">
      {steps.map((s, i) => {
        const isCurrent = i === currentStep;
        const reached = i <= furthestReached;
        const clickable = reached && !isCurrent;
        return (
          <div key={s.key} className="flex items-center">
            <Button
              variant={isCurrent ? 'primary' : reached ? 'success' : 'ghost'}
              size="sm"
              onClick={() => clickable && onJumpTo(i)}
              disabled={!clickable}
              aria-current={isCurrent ? 'step' : undefined}
              className="whitespace-nowrap rounded-full"
            >
              {reached && !isCurrent ? <Check size={12} aria-hidden="true" /> : <span>{i + 1}</span>}
              <span className="hidden sm:inline">{s.label}</span>
            </Button>
            {i < steps.length - 1 && <div className="w-6 h-px bg-surface-alt shrink-0 mx-0.5" />}
          </div>
        );
      })}
    </nav>
  );
}
